import Editor, { useMonaco } from '@monaco-editor/react';
import { useObservable } from '../utils/UseObservable';
import { currentResult, isDecompiling } from '../logic/Decompiler';
import { useEffect, useRef, useState } from 'react';
import { editor, Range } from "monaco-editor";
import { isDarkMode, isThin } from '../logic/Browser';
import { classesList } from '../logic/JarFile';
import { CodeTab, getOpenTab } from '../logic/tabs';
import { message, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { getTokenLocation } from '../logic/Tokens';
import { getNextJumpToken, nextReferenceNavigation } from '../logic/FindAllReferences';
import { setupJavaBytecodeLanguage } from '../utils/JavaBytecode';
import { IS_JAVADOC_EDITOR } from '../site';
import { applyJavadocCodeExtensions } from '../javadoc/JavadocCodeExtensions';
import { selectedInheritanceClassName } from '../logic/Inheritance';
import { createHoverProvider } from './CodeHoverProvider';
import { findTokenAtPosition } from './CodeUtils';
import {
    IS_DEFINITION_CONTEXT_KEY_NAME,
    createCopyAwAction,
    createCopyAtAction,
    createCopyMixinAction,
    createFindAllReferencesAction,
    createViewInheritanceAction
} from './CodeContextActions';
import {
    clearTokenJump,
    createDefinitionProvider,
    createEditorOpener,
    createFoldingRangeProvider,
    jumpToToken,
    pendingTokenJump
} from './CodeExtensions';
import { bytecode } from '../logic/Settings';
import { selectedFile, diffView, openTabs, selectedLines, tabHistory, referencesQuery, mobileDrawerOpen } from '../logic/State';

const IS_ANDROID_CHROME = /Android/.test(navigator.userAgent) && /Chrome/.test(navigator.userAgent);

const Code = () => {
    const monaco = useMonaco();

    const decompileResult = useObservable(currentResult);
    const classList = useObservable(classesList);
    const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
    const hideMinimap = useObservable(isThin);
    const darkMode = useObservable(isDarkMode);
    const decompiling = useObservable(isDecompiling);
    const selectedLine = useObservable(selectedLines);
    const nextReference = useObservable(nextReferenceNavigation);
    const tokenJump = useObservable(pendingTokenJump);

    const decorationsCollectionRef = useRef<editor.IEditorDecorationsCollection | null>(null);
    const lineHighlightRef = useRef<editor.IEditorDecorationsCollection | null>(null);
    const decompileResultRef = useRef(decompileResult);
    const classListRef = useRef(classList);

    const [editorInstance, setEditorInstance] = useState<editor.IStandaloneCodeEditor | null>(null);

    const [messageApi, contextHolder] = message.useMessage();

    const [resetViewTrigger, setResetViewTrigger] = useState(false);

    function applyTokenDecorations(model: editor.ITextModel) {
        if (!decompileResult) return;

        // Reapply token decorations for the current tab
        if (editorInstance && decompileResult.tokens) {
            const decorations = decompileResult.tokens.map(token => {
                const startPos = model.getPositionAt(token.start);
                const endPos = model.getPositionAt(token.start + token.length);
                const canGoTo = !token.declaration && classList && classList.includes(token.className + ".class");

                return {
                    range: new Range(startPos.lineNumber, startPos.column, endPos.lineNumber, endPos.column),
                    options: {
                        inlineClassName: token.type + '-token-decoration' + (canGoTo ? "-pointer" : "")
                    }
                };
            });

            decorationsCollectionRef.current?.clear();
            decorationsCollectionRef.current = editorInstance.createDecorationsCollection(decorations);
        }
    }

    // Keep refs updated
    useEffect(() => {
        decompileResultRef.current = decompileResult;
        classListRef.current = classList;
    }, [decompileResult, classList]);

    useEffect(() => {
        if (!monaco) return;
        monaco.editor.setTheme(darkMode ? "vs-dark" : "vs");
    }, [monaco, darkMode]);

    useEffect(() => {
        if (!monaco || !editorInstance) return;

        const definitionProvider = monaco.languages.registerDefinitionProvider(
            "java",
            createDefinitionProvider(decompileResultRef, classListRef)
        );

        const hoverProvider = monaco.languages.registerHoverProvider(
            "java",
            createHoverProvider(editorRef, decompileResultRef, classListRef)
        );

        const editorOpener = monaco.editor.registerEditorOpener(
            createEditorOpener(decompileResultRef)
        );

        const foldingRange = monaco.languages.registerFoldingRangeProvider(
            "java",
            createFoldingRangeProvider(monaco)
        );

        const copyAw = monaco.editor.addEditorAction(
            createCopyAwAction(decompileResultRef, classListRef, messageApi)
        );

        const copyAt = monaco.editor.addEditorAction(
            createCopyAtAction(decompileResultRef, classListRef, messageApi)
        );

        const copyMixin = monaco.editor.addEditorAction(
            createCopyMixinAction(decompileResultRef, classListRef, messageApi)
        );

        const viewAllReferences = monaco.editor.addEditorAction(
            createFindAllReferencesAction(decompileResultRef, classListRef, messageApi, (value) => {
                mobileDrawerOpen.next(true);
                referencesQuery.next(value);
            })
        );

        const viewInheritance = monaco.editor.addEditorAction(
            createViewInheritanceAction(decompileResultRef, messageApi, (value) => selectedInheritanceClassName.next(value))
        );

        const bytecodeLang = setupJavaBytecodeLanguage(monaco);

        return () => {
            // Dispose in the oppsite order
            bytecodeLang.dispose();
            viewInheritance.dispose();
            viewAllReferences.dispose();
            copyMixin.dispose();
            copyAt.dispose();
            copyAw.dispose();
            foldingRange.dispose();
            editorOpener.dispose();
            hoverProvider.dispose();
            definitionProvider.dispose();
        };
    }, [monaco, editorInstance, resetViewTrigger, messageApi]);

    if (IS_JAVADOC_EDITOR) {
        useEffect(() => {
            if (!monaco || !editorInstance || !decompileResult) return;

            const extensions = applyJavadocCodeExtensions(monaco, editorInstance, decompileResult);

            return () => {
                extensions.dispose();
            };
            // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
        }, [monaco, editorInstance, decompileResult]);
    }

    // Scroll to top when source changes, or to specific line if specified
    useEffect(() => {
        if (editorInstance && decompileResult) {
            lineHighlightRef.current?.clear();

            const executeScroll = () => {
                const currentLine = selectedLine?.line;
                if (currentLine) {
                    const lineEnd = selectedLine?.lineEnd ?? currentLine;
                    editorInstance.setSelection(new Range(currentLine, 1, currentLine, 1));
                    editorInstance.revealLinesInCenterIfOutsideViewport(currentLine, lineEnd);

                    // Highlight the line range
                    lineHighlightRef.current = editorInstance.createDecorationsCollection([{
                        range: new Range(currentLine, 1, lineEnd, 1),
                        options: {
                            isWholeLine: true,
                            className: 'highlighted-line',
                            glyphMarginClassName: 'highlighted-line-glyph'
                        }
                    }]);
                }
            };

            // Use requestAnimationFrame to ensure Monaco has finished layout
            requestAnimationFrame(() => {
                executeScroll();
            });
        }
    }, [decompileResult, selectedLine, editorInstance]);

    // Scroll to a "Find All References" token
    useEffect(() => {
        if (editorInstance && decompileResult) {
            if (decompileResult.language !== "java") return;

            lineHighlightRef.current?.clear();

            const executeScroll = () => {
                const nextJumpToken = getNextJumpToken(decompileResult);
                const nextJumpLocation = nextJumpToken && getTokenLocation(decompileResult, nextJumpToken);

                if (nextJumpLocation) {
                    const { line, column, length } = nextJumpLocation;
                    editorInstance.revealLinesInCenterIfOutsideViewport(line, line);
                    editorInstance.setSelection(new Range(line, column, line, column + length));
                }
            };

            requestAnimationFrame(() => {
                executeScroll();
            });
        }
    }, [decompileResult, nextReference, editorInstance]);

    // Subscribe to tab changes and store model & viewstate of previously opened tab
    useEffect(() => {
        // Cache if diffview is opened and restore if it is closed;
        const sub = diffView.subscribe((open) => {
            const openTab = getOpenTab();
            if (!(openTab instanceof CodeTab)) return;
            if (open) {
                openTab.onBlur();
            } else {
                if (!openTab) return;
                selectedFile.next(openTab.key);

                // While this is not perfect, it works because leaving the diff view
                // makes the view invisible and doesn't apply any of the custom "extensions",
                // manually forcing a rerender works ^-^
                setTimeout(() => {
                    setResetViewTrigger(!resetViewTrigger);
                }, 100);
            }
        });

        return () => {
            sub.unsubscribe();
        };
        // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
    }, []);

    // Handles setting the model and viewstate of the editor
    useEffect(() => {
        if (diffView.value) return;
        if (!monaco || !decompileResult || !editorInstance) return;

        const tab = getOpenTab();
        if (!tab || !(tab instanceof CodeTab)) return;

        // This fixes the following problem:
        // new tab opens -> setModel is set from old decompileResult.source
        // -> view is invalidated -> viewstate is lost
        // so this is quite important to keep!
        if (!tab.key.includes(decompileResult.className)) return;

        tab.editorRef = editorInstance;

        // Set new model with the current decompilation source if it's not already correct
        if (!tab.model || tab.model.isDisposed() || tab.model.getValue() !== decompileResult.source) {
            const uri = monaco.Uri.parse(`inmemory://${decompileResult.className}${bytecode.value ? '.bytecode' : '.java'}`);
            let model = monaco.editor.getModel(uri);
            if (model) {
                model.setValue(decompileResult.source);
            } else {
                model = monaco.editor.createModel(
                    decompileResult.source,
                    bytecode.value ? "bytecode" : "java",
                    uri
                );
            }
            tab.setModel(model);
        }

        // Only restore view state if there's no line to jump to
        // Otherwise the line highlighting effect will handle scrolling
        if (!selectedLine) {
            tab.applyViewToEditor(editorInstance);
        } else {
            // Just set the model without restoring view state
            if (tab.model) {
                editorInstance.setModel(tab.model);
            }
        }
        applyTokenDecorations(tab.model!);
        // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
    }, [decompileResult, resetViewTrigger, selectedLine, monaco, editorInstance]);

    // Process pending token jumps after model is loaded
    useEffect(() => {
        if (!editorInstance || !decompileResult || !tokenJump) return;

        if (decompileResult.className + ".class" === tokenJump.className) {
            requestAnimationFrame(() => {
                if (editorInstance && decompileResult) {
                    jumpToToken(decompileResult, tokenJump.targetType, tokenJump.target, editorInstance);
                    clearTokenJump();
                }
            });
        }
    }, [decompileResult, tokenJump, editorInstance]);

    // Handle gutter clicks for line linking
    useEffect(() => {
        if (!editorInstance) return;

        const onMouseDown = editorInstance.onMouseDown((e) => {
            if (e.target.type === editor.MouseTargetType.GUTTER_LINE_NUMBERS ||
                e.target.type === editor.MouseTargetType.GUTTER_GLYPH_MARGIN) {
                const lineNumber = e.target.position?.lineNumber;

                if (lineNumber) {
                    // Shift-click to select a range
                    if (e.event.shiftKey && selectedLine) {
                        selectedLines.next({ line: selectedLine.line, lineEnd: lineNumber });
                    } else {
                        selectedLines.next({ line: lineNumber });
                    }
                }
            }
        });

        return () => {
            onMouseDown.dispose();
        };
        // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
    }, [editorInstance, selectedLine]);

    return (
        <Spin
            indicator={<LoadingOutlined spin />}
            size={"large"}
            spinning={!!decompiling}
            description="Decompiling..."
            styles={{
                root: {
                    height: '100%',
                    color: 'white'
                },
                container: {
                    height: '100%',
                }
            }}
        >
            {contextHolder}
            <Editor
                defaultLanguage={"java"}
                language={decompileResult?.language}
                theme={darkMode ? "vs-dark" : "vs"}
                options={{
                    readOnly: true,
                    domReadOnly: true,
                    tabSize: 3,
                    minimap: { enabled: !hideMinimap },
                    glyphMargin: true,
                    foldingImportsByDefault: true,
                    foldingHighlight: false,
                    scrollBeyondLastLine: false,
                    editContext: IS_ANDROID_CHROME ? false : undefined, // Disable content editable on Android Chrome to attempt to stop the virtual keyboard from appearing
                }}
                onMount={(codeEditor) => {
                    setEditorInstance(codeEditor);
                    editorRef.current = codeEditor;

                    // Update context key when cursor position changes
                    // We use this to know when to show the options to copy AW/Mixin strings
                    const isDefinitionContextKey = codeEditor.createContextKey<boolean>(IS_DEFINITION_CONTEXT_KEY_NAME, false);
                    codeEditor.onDidChangeCursorPosition((e) => {
                        const token = findTokenAtPosition(codeEditor, decompileResultRef.current, classListRef.current);
                        const validToken = token != null && (token.type == "class" || token.type == "method" || token.type == "field");
                        isDefinitionContextKey.set(validToken);
                    });
                }} />
        </Spin>
    );
};

export default Code;
